import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import PostCard from './PostCard';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import CommentCard from './CommentCard';
import KebabMenuIcon from './icons/KebabMenuIcon';
import EditIcon from './icons/EditIcon';
import TrashIcon from './icons/TrashIcon';
import MapPinIcon from './icons/MapPinIcon';
import type { Application, AttendeeEntry, Opportunity } from '../types';

const OpportunityDetailPage: React.FC = () => {
  const { currentUser, managedProfile } = useAuth();
  const effectiveHandle = managedProfile?.handle ?? currentUser?.handle;
  const { viewingOpportunity, handleNavigation, handleEditOpportunity, navigationOrigin } = useNavigation();
  const { comments, postComment, deleteOpportunity, attendOpportunity } = useOpportunities();
  const [newComment, setNewComment] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!viewingOpportunity) {
    return (
      <div className="text-center p-8 bg-brand-surface rounded-lg">
        <h2 className="text-xl font-bold">Opportunity Not Found</h2>
        <p className="text-brand-muted">This opportunity may have been removed.</p>
        <button onClick={() => handleNavigation('feed')} className="mt-4 bg-brand-orange text-white font-bold py-2 px-4 rounded-full tracking-widest text-sm">Back to Feed</button>
      </div>
    );
  }
  
  const isAuthor = effectiveHandle === viewingOpportunity.author.handle;
  const isFan = (managedProfile ?? currentUser)?.subscriptionTier === 'fan_basic';
  // Use optional chaining and default empty array to prevent crashes
  const myApplications = (viewingOpportunity.applications || []).filter((app: Application) => app.applicant.handle === effectiveHandle);
  const hasApplied = myApplications.length > 0;

  const FAN_ALLOWED_ATTEND_TYPES: Opportunity['type'][] = [
    'Gig', 'Event', 'Jam Session', 'Live Show', 'Stage Play', 'Open Mic',
    'DJ set', 'Stand Up Comedy', 'Workshop', 'Presentation', 'Vernissage', 'Collective Exhibition'
  ];
  const isAttendableType = FAN_ALLOWED_ATTEND_TYPES.includes(viewingOpportunity.type);
  const attendees: AttendeeEntry[] = viewingOpportunity.attendees || [];
  const attendeeCount = viewingOpportunity.attendeeCount ?? attendees.length;
  const maxCapacity = viewingOpportunity.maxCapacity;
  const isAttending = !!effectiveHandle && attendees.some(a => a.handle === effectiveHandle);
  const isFull = maxCapacity != null && attendeeCount >= maxCapacity;
  const showAttendSection = !isAuthor && !!effectiveHandle && (isAttendableType || !!maxCapacity);

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    postComment(viewingOpportunity.id, newComment.trim());
    setNewComment('');
  };

  const onNavigateToApplication = () => {
    if (!viewingOpportunity) return;
    handleNavigation('applicationDetail', { opportunity: viewingOpportunity });
  }

  const handleDelete = () => {
    deleteOpportunity(viewingOpportunity.id);
    handleNavigation('feed');
  }
  
  const handleViewOnMap = () => {
    if (viewingOpportunity.coordinates) {
      const { lat, lng } = viewingOpportunity.coordinates;
      window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
    } else if (viewingOpportunity.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(viewingOpportunity.address)}`, '_blank');
    }
  };

  return (
    <div className="space-y-6 font-urbanist">
       <div className="flex justify-between items-center">
        <button onClick={() => handleNavigation(navigationOrigin || 'feed')} className="flex items-center space-x-2 text-brand-muted hover:text-white font-semibold">
           <ArrowLeftIcon className="h-5 w-5" />
           <span>Back</span>
       </button>
        {isAuthor && (
          <div className="relative" ref={menuRef}>
            <button onClick={() => setIsMenuOpen(prev => !prev)} className="p-2 rounded-full hover:bg-gray-700"><KebabMenuIcon className="h-6 w-6 text-brand-muted"/></button>
            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-brand-surface rounded-md shadow-lg py-1 border border-gray-700/50 z-10">
                <button onClick={() => handleEditOpportunity(viewingOpportunity)} className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"><EditIcon className="h-4 w-4 mr-2" /> Edit Opportunity</button>
                <button onClick={handleDelete} className="w-full text-left flex items-center px-4 py-2 text-sm text-red-400 hover:bg-gray-700"><TrashIcon className="h-4 w-4 mr-2" /> Delete Opportunity</button>
              </div>
            )}
          </div>
        )}
       </div>

      <PostCard opportunity={viewingOpportunity} />

      {showAttendSection && (
        <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white tracking-widest mb-1">
                {isAttending ? 'You are attending' : 'Attend this event'}
              </h3>
              {maxCapacity != null ? (
                <div className="space-y-2 mt-2">
                  <p className="text-sm text-brand-muted font-semibold">
                    <span className={`font-bold ${isFull && !isAttending ? 'text-red-400' : 'text-white'}`}>{attendeeCount}</span>
                    {' / '}<span className="text-white">{maxCapacity}</span>{' spots taken'}
                    {isFull && !isAttending && <span className="ml-2 text-red-400 font-black text-xs uppercase tracking-wider">FULL</span>}
                  </p>
                  <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${isFull ? 'bg-red-500' : attendeeCount / maxCapacity > 0.75 ? 'bg-yellow-500' : 'bg-brand-orange'}`}
                      style={{ width: `${Math.min((attendeeCount / maxCapacity) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-brand-muted mt-1">
                  {attendeeCount > 0 ? `${attendeeCount} ${attendeeCount === 1 ? 'person' : 'people'} attending` : 'Be the first to attend!'}
                </p>
              )}
            </div>
            <button
              onClick={() => attendOpportunity(viewingOpportunity.id)}
              disabled={isFull && !isAttending}
              className={`flex-shrink-0 font-bold py-3 px-6 rounded-full tracking-widest text-xs transition-all shadow-lg
                ${isAttending
                  ? 'bg-gray-700 text-white hover:bg-red-900/50 hover:text-red-400 border border-gray-600'
                  : isFull
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700'
                    : 'bg-brand-orange text-white hover:bg-orange-600 shadow-brand-orange/20'
                }`}
            >
              {isAttending ? 'Cancel Attendance' : isFull ? 'Event Full' : 'Attend'}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-brand-surface rounded-lg p-6 flex flex-col justify-between">
            <div>
                <h3 className="text-lg font-bold text-white mb-4 tracking-widest">Location & Venue</h3>
                <div className="space-y-3">
                    <div className="flex items-start gap-3">
                        <MapPinIcon className="h-6 w-6 text-brand-orange mt-1 flex-shrink-0" />
                        <div>
                            <p className="text-xl font-bold text-white">{viewingOpportunity.location}</p>
                            {viewingOpportunity.address && (
                                <p className="text-brand-muted leading-relaxed mt-1">{viewingOpportunity.address}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <button onClick={handleViewOnMap} className="mt-6 w-full bg-gray-700 text-white font-bold py-3 px-4 rounded-full hover:bg-gray-600 transition-colors tracking-widest text-xs border border-gray-600 shadow-sm">
                Open in Google Maps
            </button>
        </div>

        {!isFan && (
            <div className="bg-brand-surface rounded-lg p-6 flex flex-col justify-between">
                <div>
                    <h3 className="text-lg font-bold text-white mb-4 tracking-widest">Application</h3>
                    <p className="text-sm text-brand-muted mb-4">
                        {isAuthor 
                            ? `You have received ${viewingOpportunity.applicationCount ?? 0} applications for this role.` 
                            : hasApplied 
                                ? "You have already submitted an application. You can view or add more entities (like your bands)." 
                                : "Submit your EPK or band profile to this opportunity to be considered by the organizer."}
                    </p>
                </div>
                {isAuthor ? (
                    <button
                        onClick={() => handleNavigation('applicantList', { opportunity: viewingOpportunity })}
                        className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-full hover:bg-blue-500 transition-opacity tracking-widest text-xs shadow-lg shadow-blue-500/20"
                    >
                        View Applicants ({viewingOpportunity.applicationCount ?? 0})
                    </button>
                ) : (
                    <button
                        onClick={onNavigateToApplication}
                        className="w-full bg-brand-orange text-white font-bold py-3 px-4 rounded-full hover:bg-orange-600 transition-opacity tracking-widest text-xs shadow-lg shadow-brand-orange/20"
                    >
                        {hasApplied ? 'Manage / Add Application' : 'Apply Now'}
                    </button>
                )}
            </div>
        )}
      </div>

      <div className="bg-brand-surface rounded-lg p-6">
        <h3 className="text-xl font-bold mb-4 tracking-tight">Comments ({viewingOpportunity.comments})</h3>
        <div className="border-t border-gray-700/50 pt-4 space-y-4">
            {!isFan && currentUser && (
                 <form onSubmit={handleCommentSubmit} className="flex items-start space-x-3">
                    <img src={currentUser.avatarUrl} alt={currentUser.name} className="h-10 w-10 rounded-full border border-gray-700"/>
                    <div className="flex-1">
                        <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-1 focus:ring-brand-orange resize-none text-sm font-medium border border-gray-600"
                            rows={2}
                        />
                        <div className="flex justify-end mt-2">
                            <button
                                type="submit"
                                disabled={!newComment.trim()}
                                className="bg-brand-orange text-white font-bold px-6 py-2 rounded-full hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all tracking-widest text-[10px]"
                            >
                                Post Comment
                            </button>
                        </div>
                    </div>
                 </form>
            )}
            {comments.length > 0 ? (
                <div className="space-y-4">
                    {comments.map(comment => (
                        <CommentCard key={comment.id} comment={comment} />
                    ))}
                </div>
            ) : (
                <p className="text-center text-brand-muted py-4 italic text-sm">
                    {isFan ? 'No comments yet.' : 'No comments yet. Be the first to start the conversation!'}
                </p>
            )}
        </div>
      </div>

      {isAuthor && (isAttendableType || !!maxCapacity) && (
        <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
          <h3 className="text-lg font-bold text-white tracking-widest mb-4">
            Attendees <span className="text-brand-orange font-black">{attendeeCount}</span>
            {maxCapacity != null && <span className="text-brand-muted text-sm font-medium"> / {maxCapacity}</span>}
          </h3>
          {attendees.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {attendees.map(attendee => (
                <div key={attendee.handle} className="flex items-center gap-3 p-3 bg-gray-800/40 rounded-xl border border-gray-700/50">
                  <img src={attendee.avatarUrl} alt={attendee.name} className="h-10 w-10 rounded-full border border-gray-700 object-cover flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate uppercase tracking-tight">{attendee.name}</p>
                    <p className="text-[10px] text-brand-muted font-mono">@{attendee.handle}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-brand-muted py-6 italic text-sm">No attendees yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default OpportunityDetailPage;